/**
 * The product page: the gallery (blocks/asshine-product-gallery.liquid) and
 * the buy box (blocks/asshine-buy-box.liquid).
 *
 * The buy box's "Pick your pack" radios choose between two products, so
 * Horizon's variant picker does not drive them. Picking a pack writes its
 * variant into the product form's hidden `id` input, switches the labels that
 * follow the pack, shows the pack's photo and puts the pack's URL in the
 * address bar, so a shared link opens on the same pack.
 *
 * Shopify's express checkout buttons are rendered for one product
 * (`{{ form | payment_button }}`), so they are swapped for the picked pack's,
 * fetched from that pack's page through the Section Rendering API. They are
 * hidden from the click until the swap lands, so they never check out the
 * pack that was not picked.
 */

const PACK_EVENT = 'asshine:pack';

class AsshineGallery extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(PACK_EVENT, this.#onPack);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(PACK_EVENT, this.#onPack);
  }

  /** @param {string} index */
  show(index) {
    for (const image of this.querySelectorAll('.asshine-gallery__image')) {
      const active = image instanceof HTMLElement && image.dataset.index === index;
      image.classList.toggle('is-active', active);
      if (active && image instanceof HTMLImageElement) image.loading = 'eager';
    }
    for (const thumb of this.querySelectorAll('.asshine-gallery__thumb')) {
      if (thumb instanceof HTMLElement) thumb.setAttribute('aria-pressed', String(thumb.dataset.index === index));
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('.asshine-gallery__thumb') : null;
    if (thumb instanceof HTMLElement && thumb.dataset.index) this.show(thumb.dataset.index);
  };

  /** @param {Event} event */
  #onPack = (event) => {
    const pack = event instanceof CustomEvent ? event.detail?.pack : null;
    const image = pack ? this.querySelector(`.asshine-gallery__image[data-pack="${pack}"]`) : null;
    if (image instanceof HTMLElement && image.dataset.index) this.show(image.dataset.index);
  };
}

class AsshineBuyBox extends HTMLElement {
  /** @type {AbortController | undefined} */
  #expressFetch;
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);

    const sticky = this.querySelector('[data-sticky]');
    const button = this.querySelector('.asshine-buy-box__button');
    if (!sticky || !button) return;
    const footer = document.querySelector('footer');

    // Shown once the main button has scrolled off the top (not before it is
    // reached), hidden again over the footer. A scroll listener, not an
    // IntersectionObserver: an observer only reports crossings, and a jump
    // (End, a dragged scrollbar) can carry the button and the whole buy box
    // from below the fold to above it without crossing anything. Captured on
    // document because from 990px Horizon scrolls .page-wrapper, not the
    // window. One read per frame, and the bar is touched only on a change.
    let shown = false;
    let frame = 0;
    const update = () => {
      frame = 0;
      const above = button.getBoundingClientRect().bottom < 0;
      const overFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const next = above && !overFooter;
      if (next === shown) return;
      shown = next;
      sticky.classList.toggle('is-shown', shown);
      sticky.toggleAttribute('inert', !shown);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    this.#listeners = new AbortController();
    const options = { capture: true, passive: true, signal: this.#listeners.signal };
    document.addEventListener('scroll', schedule, options);
    window.addEventListener('resize', schedule, options);
    schedule();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#listeners?.abort();
    this.#expressFetch?.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio' || !radio.dataset.pack) return;

    // By attribute: the form has an input named "id", which shadows form.id.
    const form = document.getElementById(this.dataset.form ?? '');
    if (!(form instanceof HTMLFormElement)) return;

    const id = form.querySelector('input[name="id"]');
    if (id instanceof HTMLInputElement) id.value = radio.value;
    const productId = form.querySelector('input[name="product-id"]');
    if (productId instanceof HTMLInputElement && radio.dataset.productId) productId.value = radio.dataset.productId;
    const productForm = form.closest('product-form-component');
    if (productForm instanceof HTMLElement) {
      if (radio.dataset.productId) productForm.dataset.productId = radio.dataset.productId;
      if (radio.dataset.url) productForm.dataset.productUrl = radio.dataset.url;
    }

    this.dataset.pack = radio.dataset.pack;
    if (radio.dataset.url) {
      const url = new URL(radio.dataset.url, location.origin);
      url.search = location.search;
      url.searchParams.delete('variant');
      history.replaceState(history.state, '', url);
      this.#swapExpress(form, url);
    }
    document.dispatchEvent(new CustomEvent(PACK_EVENT, { detail: { pack: radio.dataset.pack } }));
  };

  /**
   * @param {HTMLFormElement} form
   * @param {URL} packUrl - The picked pack's page.
   */
  async #swapExpress(form, packUrl) {
    const express = form.querySelector('.asshine-buy-box__express');
    if (!(express instanceof HTMLElement) || !this.dataset.sectionId) return;

    express.hidden = true;
    this.#expressFetch?.abort();
    const controller = new AbortController();
    this.#expressFetch = controller;

    const url = new URL(packUrl);
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector(`#${CSS.escape(this.dataset.form ?? '')} .asshine-buy-box__express`);
      if (fresh && !controller.signal.aborted) express.replaceWith(fresh);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.warn('[asshine-buy-box]', error);
    }
  }
}

if (!customElements.get('asshine-gallery')) customElements.define('asshine-gallery', AsshineGallery);
if (!customElements.get('asshine-buy-box')) customElements.define('asshine-buy-box', AsshineBuyBox);
